import React, { useState } from 'react';
import { Music, Play, ExternalLink, X, Instagram } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { portfolioItems, companyInfo } from '../data/mock';

const Portfolio = () => {
  const [selectedVideo, setSelectedVideo] = useState(null);

  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-[#DFD6CD]/20">
      {/* Modal para vídeo do Instagram */}
      {selectedVideo && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-2 sm:p-4" onClick={() => setSelectedVideo(null)}>
          <div className="relative w-full max-w-sm" onClick={(e) => e.stopPropagation()}>
            <button
              onClick={() => setSelectedVideo(null)}
              className="absolute -top-10 right-0 text-white hover:text-[#CDAD1E] transition-colors z-10"
            >
              <X size={28} />
            </button>
            <div className="bg-white rounded-lg overflow-hidden">
              {/* Iframe do Instagram mais compacto */}
              <iframe
                src={`https://www.instagram.com/reel/${selectedVideo.instagramUrl.match(/reel\/([^/?]+)/)?.[1]}/embed`}
                className="w-full"
                height="450"
                frameBorder="0"
                scrolling="no"
                allowTransparency="true"
                allow="autoplay; encrypted-media"
                title={selectedVideo.title}
                style={{ maxHeight: '70vh' }}
              ></iframe>
            </div>
            {/* Instrução para reproduzir */}
            <div className="text-center mt-3 space-y-2">
              <p className="text-white/80 text-sm flex items-center justify-center gap-2">
                <Play size={14} />
                Clique na imagem para reproduzir
              </p>
              <a
                href="https://www.instagram.com/harmonianupcialbsb"
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#CDAD1E] hover:text-white text-xs flex items-center justify-center gap-1"
              >
                <Instagram size={14} />
                Abrir no Instagram
              </a>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <section className="pt-32 pb-16 px-4 bg-gradient-to-br from-[#DFD6CD] to-white">
        <div className="container mx-auto text-center">
          <div className="inline-block mb-6">
            <span className="inline-flex items-center gap-2 px-4 py-2 bg-white/60 backdrop-blur-sm rounded-full text-sm font-medium text-[#920216] border border-[#CDAD1E]/30">
              <Music size={16} className="text-[#B30310]" />
              Nosso Trabalho
            </span>
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-[#920216] mb-6">
            Portfólio Musical
          </h1>
          <p className="text-lg text-gray-700 max-w-3xl mx-auto leading-relaxed">
            Conheça nossos covers gravados ao vivo durante cerimônias reais.
            Cada execução é feita com cuidado, técnica e muito amor.
          </p>
        </div>
      </section>

      {/* Portfolio Grid */}
      <section className="py-16 px-4">
        <div className="container mx-auto max-w-6xl">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {portfolioItems.map((item) => (
              <Card 
                key={item.id} 
                className="border-[#CDAD1E]/30 hover:shadow-xl transition-all duration-300 group overflow-hidden cursor-pointer"
                onClick={() => item.instagramUrl && setSelectedVideo(item)}
              >
                <CardContent className="p-0">
                  {/* Video Thumbnail */}
                  <div className="relative h-48 bg-gradient-to-br from-[#920216] to-[#B30310] flex items-center justify-center overflow-hidden">
                    <div className="absolute inset-0 bg-black/20"></div>
                    <Music className="text-white/80 group-hover:scale-110 transition-transform duration-300" size={64} />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                    
                    {/* Play Button Overlay */}
                    <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300 bg-black/40">
                      <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center shadow-lg transform group-hover:scale-110 transition-transform">
                        {item.instagramUrl ? (
                          <Instagram className="text-[#B30310]" size={28} />
                        ) : (
                          <Play className="text-[#B30310] ml-1" size={28} fill="currentColor" />
                        )}
                      </div>
                    </div>

                    {/* Moment Badge */}
                    <div className="absolute top-4 left-4">
                      <Badge className="bg-white/90 text-[#920216]">
                        {item.moment}
                      </Badge>
                    </div>

                    {/* Type Badge */}
                    <div className="absolute top-4 right-4">
                      <Badge className="bg-[#CDAD1E] text-white capitalize">
                        {item.type}
                      </Badge>
                    </div>

                    {/* Live Recording Badge */}
                    <div className="absolute bottom-4 left-4">
                      <Badge className="bg-red-600 text-white text-xs">
                        Gravação ao vivo
                      </Badge>
                    </div>
                  </div>

                  {/* Content */}
                  <div className="p-6">
                    <h3 className="text-xl font-semibold text-[#920216] mb-2">
                      {item.title}
                    </h3>
                    <p className="text-sm text-gray-600 mb-3">{item.artist}</p>
                    <p className="text-sm text-gray-700 leading-relaxed">
                      {item.description}
                    </p>
                    {item.instagramUrl && (
                      <p className="text-xs text-[#B30310] mt-3 flex items-center gap-1">
                        <Instagram size={14} />
                        Clique para assistir
                      </p>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Instagram CTA */}
          <Card className="mt-16 bg-gradient-to-r from-[#920216] to-[#B30310] border-none text-white">
            <CardContent className="p-8 md:p-12 text-center">
              <div className="max-w-2xl mx-auto">
                <h2 className="text-2xl md:text-3xl font-bold mb-4">
                  Veja Mais no Instagram
                </h2>
                <p className="text-lg mb-8 opacity-90">
                  Acesse nosso perfil no Instagram para ouvir mais músicas, ver vídeos de apresentações
                  e acompanhar nosso trabalho de perto.
                </p>
                <Button
                  asChild
                  size="lg"
                  className="bg-white text-[#920216] hover:bg-[#DFD6CD] transition-all duration-300 shadow-lg"
                >
                  <a
                    href={`https://instagram.com/${companyInfo.instagram.replace('@', '')}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2"
                  >
                    <ExternalLink size={20} />
                    Visitar @harmonianupcialbsb
                  </a>
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>
    </div>
  );
};

export default Portfolio;